import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { graphql } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { styled } from '@compiled/react';
// We have deprecated unstated. Please use react-sweet-state instead
import { Subscribe } from 'unstated';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import FadeIn from '@atlaskit/motion/fade-in';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { cssMap } from '@atlaskit/css';
import ClockIcon from '@atlaskit/icon/core/clock';
import { abortAll } from '@atlaskit/react-ufo/interaction-metrics';
import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { isUnauthorizedError as is403 } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { isUnauthorizedError as is401 } from '@confluence/error-types/entry-points/isUnauthorizedError.ts';
import { RESTRICTED_PAGE_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
	ExperienceFailure,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { shouldShowMobileWeb } from '@confluence/mobile-detection';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { VIEW_PAGE, HOME } from '@confluence/named-routes/entry-points/routes.ts';
import { ErrorPageComponent } from '@confluence/not-found/entry-points/ErrorPageComponent.ts';
import { useContentType } from '@confluence/page-context';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useFeatureCodesInURL } from '@confluence/feature-codes/entry-points/useFeatureCodesInURL.ts';
import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { usePendingRequestExists } from '@confluence/access-requests/entry-points/usePendingRequestExists.ts';
import { useAccessRequestSettings } from '@confluence/access-requests/entry-points/useAccessRequestSettings.ts';
import { AccessDisabledMessage } from '@confluence/access-requests/entry-points/AccessDisabledMessage.ts';
import { hasCustomAccessMessage } from '@confluence/access-requests/entry-points/hasCustomAccessMessage.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import {
	expValEquals,
	expVal,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';

import type {
	RestrictedPageWithCanViewQuery$data,
	RestrictedPageWithCanViewQuery$variables,
} from './__generated__/RestrictedPageWithCanViewQuery.graphql.ts';
import { INACCESSIBLE_PAGE_LOAD } from './perf.config.js';
import CantViewLightMode from './CantViewLight.svg';
import CantViewDarkMode from './CantViewDark.svg';
import CantEditLightMode from './CantEditLight.svg';
import CantEditDarkMode from './CantEditDark.svg';
import CantViewLightModeMohiti from './CantViewLightMohiti.svg';
import CantViewDarkModeMohiti from './CantViewDarkMohiti.svg';
import CantEditLightModeMohiti from './CantEditLightMohiti.svg';
import CantEditDarkModeMohiti from './CantEditDarkMohiti.svg';
import { SuperAdminGateway } from './SuperAdminGateway.js';
import type {
	RestrictedPageRequestPageAccessMutation$data,
	RestrictedPageRequestPageAccessMutation$variables,
} from './__generated__/RestrictedPageRequestPageAccessMutation.graphql.ts';
import { ShowSpinnerAfterDelay } from './ShowSpinnerAfterDelay.tsx';
import { RestrictedSpace } from './RestrictedSpace.tsx';

const RestrictedPageWithCanViewQueryGraphQL = graphql`
	query RestrictedPageWithCanViewQuery($id: ID!) {
		singleContent(id: $id) {
			id
			status
			links {
				webui
			}
		}
		user: confluence_atlassianUser(current: true) {
			id
			confluence {
				roles {
					canBeSuperAdmin
				}
			}
		}
	}
`;

const RestrictedPageRequestPageAccessMutationGraphQL = graphql`
	mutation RestrictedPageRequestPageAccessMutation(
		$requestPageAccessInput: RequestPageAccessInput!
	) {
		requestPageAccess(requestPageAccessInput: $requestPageAccessInput) {
			displayName
		}
	}
`;

const SUCCESS_FLAG_ID = 'reqestAccessSuccess';

type RestrictedPageProps = {
	accessType: 'inherited' | 'view' | 'edit';
	contentId: string | undefined | null;
	showSuperAdminGateway?: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContainer = styled.div<{ isMobile: boolean; align?: 'default' | 'center' }>({
	marginTop: token('space.300'),
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	justifyContent: ({ isMobile, align }) => (align === 'center' || isMobile ? 'center' : 'left'),
	gap: token('space.100'),
});

const styles = cssMap({
	buttonPlaceholder: {
		marginLeft: token('space.100'),
		height: '32px',
		width: '32px',
		display: 'flex',
		alignItems: 'center',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RequestConfirmationTextContainer = styled.div({
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
	height: '32px',
	paddingLeft: token('space.100'),
	paddingRight: token('space.100'),
});

const CUSTOM_ATTRIBUTES = {
	inaccessiblePageType: 'RESTRICTED_PAGE',
};

const i18n = defineMessages({
	requestSentTitle: {
		id: 'no-permission.restricted.page.request.access.successful.title.2',
		defaultMessage: 'Request sent',
		description: 'Title of notification after request successfully sent',
	},
	reminderSentTitle: {
		id: 'no-permission.restricted.page.request.access.reminder-sent.title',
		defaultMessage: 'Reminder sent',
		description: 'Title of notification after an access request reminder is successfully sent',
	},
	requestFailedTitle: {
		id: 'no-permission.restricted.page.request.access.failed.title',
		defaultMessage: 'Access request unsuccessful',
		description: 'Title of notification after request failed',
	},
	reminderFailedTitle: {
		id: 'no-permission.restricted.page.request.access.reminder-failed.title',
		defaultMessage: 'Reminder not sent',
		description: 'Title of notification after an access request reminder fails to send',
	},
	requestFailedBody: {
		id: 'no-permission.restricted.page.request.access.failed.message.2',
		defaultMessage:
			'We ran into a problem submitting your access request. Try again or contact your admin.',
		description: 'Message body of notification after request failed',
	},
	reminderFailedBody: {
		id: 'no-permission.restricted.page.request.access.reminder-failed.message',
		defaultMessage: 'We ran into a problem sending your reminder. Try again or contact your admin.',
		description: 'Message body of notification after an access request reminder fails to send',
	},
	// No "for now": unlike the request-access screen, this one offers no way to ask, so a title
	// implying the lock is temporary would promise a path that isn't there.
	lockedTitle: {
		id: 'no-permission.restricted.page.access-requests-disabled.view.title',
		defaultMessage: 'This content is locked',
		description:
			'Title shown on the restricted content screen when content access requests are disabled by admin',
	},
	editingLockedTitle: {
		id: 'no-permission.restricted.page.access-requests-disabled.edit.title',
		defaultMessage: 'Editing is locked',
		description:
			'Title shown on the restricted content screen when the user cannot edit the content and content access requests are disabled by admin',
	},
	viewFallback: {
		id: 'no-permission.restricted.page.access-requests-disabled.view.message',
		defaultMessage: 'You don’t have permission to view this content.',
		description:
			'Default message shown on the restricted content screen when content access requests are disabled and no custom message is configured',
	},
	editFallback: {
		id: 'no-permission.restricted.page.access-requests-disabled.edit.message',
		defaultMessage: 'You don’t have permission to edit this content.',
		description:
			'Default message shown on the restricted content screen when the user cannot edit the content, content access requests are disabled, and no custom message is configured',
	},
});

const LICENSED_ACCESS_STATUSES = new Set<AccessStatus>([
	AccessStatus.LICENSED_ADMIN_ACCESS,
	AccessStatus.LICENSED_USE_ACCESS,
	AccessStatus.EXTERNAL_COLLABORATOR_ACCESS,
]);

const restrictedContentIllustration = {
	light: CantViewLightMode,
	dark: CantViewDarkMode,
};

const restrictedContentMohitiIllustration = {
	light: CantViewLightModeMohiti,
	dark: CantViewDarkModeMohiti,
};

const requestEditAccessIllustration = {
	light: CantEditLightMode,
	dark: CantEditDarkMode,
};

const requestEditAccessMohitiIllustration = {
	light: CantEditLightModeMohiti,
	dark: CantEditDarkModeMohiti,
};

type AccessRequestSubmitIntent = 'request' | 'reminder';

const RestrictedPageComponent = ({
	accessType,
	contentId,
	editDeniedButCanView,
	flags,
	viewLink,
	showSuperAdminGateway = true,
	isAccessRequestsDisabled,
	messageContent,
	areAccessRequestSettingsLoading,
}: RestrictedPageProps &
	WithFlagsProps & {
		editDeniedButCanView: boolean;
		viewLink: string | null;
		isAccessRequestsDisabled: boolean;
		messageContent: string | null | undefined;
		areAccessRequestSettingsLoading: boolean;
	}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [contentType] = useContentType();
	const [requestSent, setRequestSent] = useState(false);
	const [reminderSent, setReminderSent] = useState(false);
	const accessRequestSubmitIntentRef = useRef<AccessRequestSubmitIntent>('request');
	const renderServerPlaceholder = useIsSSROrHydrating();
	const { accessStatus } = useSessionData();

	const {
		pendingRequestExists,
		isLoadingForFirstTime,
		isPendingRequestResolvedForSSR,
		error: pendingAccessRequestExistsError,
	} = usePendingRequestExists({
		contentId,
		preferPreloadedData: expValEquals(
			'cc_mohiti_restricted_content_screen_ab_exp',
			'isEnabled',
			true,
		),
	});

	const { from } = useFeatureCodesInURL();

	const makeLinkClickedHandler = (buttonLocation: string) => () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'viewPageButton',
				source: 'restrictedPageScreen',
				attributes: {
					buttonLocation,
					accessType: accessType || 'unspecified',
					contentId,
					contentType,
				},
			},
		}).fire();

		// If the link was in a flag, hide the flag, since this might be an
		// SPA transition.
		void flags.hideFlag(SUCCESS_FLAG_ID);
	};

	const handleGoHomeClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'restrictedPageGoHomeButton',
				source: 'restrictedPageScreen',
				attributes: {
					accessType: accessType || 'unspecified',
					contentId,
					contentType,
					editDeniedButCanView,
				},
			},
		}).fire();
	};

	/** Displays the success or failure flags after access is requested */
	const handleRequestComplete = (result: RestrictedPageRequestPageAccessMutation$data) => {
		const requestSuccessful = result?.requestPageAccess?.displayName;
		const isReminder = accessRequestSubmitIntentRef.current === 'reminder';

		if (requestSuccessful) {
			void flags.showSuccessFlag({
				isAutoDismiss: true,
				id: SUCCESS_FLAG_ID,
				title: formatMessage(isReminder ? i18n.reminderSentTitle : i18n.requestSentTitle),
				actions: editDeniedButCanView
					? [
							{
								content: (
									<FormattedMessage
										id="no-permission.restricted.page.flag.view-content"
										description="Link button label for viewing a page/blog/whiteboard/etc. the user can't edit"
										defaultMessage="View the content now"
									/>
								),
								onClick: makeLinkClickedHandler('access-requested-flag'),
								href: viewLink!,
							},
						]
					: undefined,
			});
			if (isReminder) {
				setReminderSent(true);
			} else {
				setRequestSent(true);
			}
		} else {
			void flags.showErrorFlag({
				isAutoDismiss: true,
				title: formatMessage(isReminder ? i18n.reminderFailedTitle : i18n.requestFailedTitle),
				description: formatMessage(isReminder ? i18n.reminderFailedBody : i18n.requestFailedBody),
			});
		}
	};

	const handleRequestError = () => {
		const isReminder = accessRequestSubmitIntentRef.current === 'reminder';

		void flags.showErrorFlag({
			isAutoDismiss: true,
			title: formatMessage(isReminder ? i18n.reminderFailedTitle : i18n.requestFailedTitle),
			description: formatMessage(isReminder ? i18n.reminderFailedBody : i18n.requestFailedBody),
		});
	};

	const [requestAccess] = useMutationInterop<
		RestrictedPageRequestPageAccessMutation$data,
		RestrictedPageRequestPageAccessMutation$variables
	>(RestrictedPageRequestPageAccessMutationGraphQL, {
		variables: {
			requestPageAccessInput: {
				pageId: contentId ?? '',
				accessType: accessType === 'edit' ? 'EDIT' : 'VIEW',
			},
		},
		onCompleted: handleRequestComplete,
		onError: handleRequestError,
	});

	const renderTitle = () => {
		if (accessType !== 'edit') {
			if (expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
				return (
					<FormattedMessage
						id="no-permission.restricted.page.view.title.mohiti"
						description="Title displayed for a page that has viewing restricted in the Mohiti restricted content experience"
						defaultMessage="This content is locked for now"
					/>
				);
			}

			return (
				<FormattedMessage
					id="no-permission.restricted.page.view.title"
					description="Title displayed for a page that has viewing restricted"
					defaultMessage="You’ve stumbled on restricted content"
				/>
			);
		}

		if (editDeniedButCanView) {
			return (
				<FormattedMessage
					id="no-permission.restricted.page.edit-but-can-view.title"
					description="Title displayed for a page which the user cannot edit but could view"
					defaultMessage="You don’t have access to edit this content"
				/>
			);
		}

		if (expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
			return (
				<FormattedMessage
					id="no-permission.restricted.page.edit.title.mohiti"
					description="Title displayed for a page that has editing restricted in the Mohiti restricted content experience"
					defaultMessage="Ask the owner to pass the pen"
				/>
			);
		}

		return (
			<FormattedMessage
				id="no-permission.restricted.page.edit.title"
				description="Title displayed for a page that has editing restricted"
				defaultMessage="Editing is restricted for this content"
			/>
		);
	};

	const renderMessage = () =>
		accessType === 'inherited' &&
		!expVal('confluence_access_request_updates', 'isEnabled', false) ? (
			<FormattedMessage
				id="no-permission.restricted.page.inherited.message"
				description="Message displayed when a page/content restriction is inherited from parent"
				defaultMessage="This is because it’s inheriting restrictions from a parent item. A space admin or the person who shared this content may be able to give you access."
			/>
		) : (
			renderAccessRequest()
		);

	const renderPendingAccessMessage = () => (
		<FormattedMessage
			id="no-permission.restricted.page.pending-access.message"
			description="Confirmation text shown when a user has already requested view access to restricted content"
			defaultMessage="We’ve already notified the owner that you need access. If you’re in a hurry, you can send a quick nudge."
		/>
	);

	const submitAccessRequest = async (intent: AccessRequestSubmitIntent) => {
		if (intent === 'reminder' && reminderSent) {
			return;
		}

		try {
			accessRequestSubmitIntentRef.current = intent;
			setRequestAccessButtonLoading(true);
			setAnimateTextDirection(true);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: intent === 'reminder' ? 'sendReminderButton' : 'requestAccessButton',
					source: 'restrictedPageScreen',
					attributes: {
						accessType: accessType || 'unspecified',
						contentId,
						contentType,
						editDeniedButCanView,
						submitIntent: intent,
					},
				},
			}).fire();

			await requestAccess();
		} catch {
			// handle the error
		}

		setRequestAccessButtonLoading(false);
	};

	const handleRequestAccessClick = () => submitAccessRequest('request');
	const handleSendReminderClick = () => submitAccessRequest('reminder');

	const [animateTextDirection, setAnimateTextDirection] = useState(false);
	const [requestAccessButtonLoading, setRequestAccessButtonLoading] = useState(false);

	const renderRequestSentConfirmation = () => (
		<FadeIn
			entranceDirection={animateTextDirection ? 'right' : undefined}
			distance="proportional"
			key="confirmation-text-animation"
		>
			{({ ...props }) => (
				<RequestConfirmationTextContainer {...props}>
					<Inline alignBlock="center" space="space.050" grow="hug">
						<ClockIcon label="" color={token('color.icon.subtlest')} size="small" />
						<Text color="color.text.subtlest" weight="medium">
							{accessType === 'edit' && !pendingRequestExists ? (
								<FormattedMessage
									id="no-permission.restricted.page.request.edit-access.request-sent-confirmation"
									defaultMessage="Edit requested"
									description="Confirmation message shown after edit request has been sent"
								/>
							) : (
								// This serves double purpose for confirming VIEW requests or
								// Confirming there's an existing request of unknown accessType
								// This happens when the page loads and we call the API to know if there's a request.
								// Currently it doesn't tell us what request type it is, so we make it sound generic
								// Until the API gets updated, which should be easy
								<FormattedMessage
									id="no-permission.restricted.page.request.view-access.request-sent-confirmation"
									defaultMessage="Access requested"
									description="Confirmation message shown after view request has been sent"
								/>
							)}
						</Text>
					</Inline>
				</RequestConfirmationTextContainer>
			)}
		</FadeIn>
	);

	const renderRequestAccessButtonMessage = () => {
		if (accessType === 'edit') {
			if (expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
				return (
					<FormattedMessage
						id="no-permission.restricted.page.request.edit-access.mohiti"
						description="Button label for requesting edit access to restricted content in the Mohiti restricted content experience"
						defaultMessage="Request edit access"
					/>
				);
			}

			return (
				<FormattedMessage
					id="no-permission.restricted.page.request.edit-access"
					description="Button label for requesting edit access to a page"
					defaultMessage="Ask to edit"
				/>
			);
		}

		if (expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
			return (
				<FormattedMessage
					id="no-permission.restricted.page.request.view-access.mohiti"
					description="Button label for requesting view access to restricted content in the Mohiti restricted content experience"
					defaultMessage="Request access"
				/>
			);
		}

		return (
			<FormattedMessage
				id="no-permission.restricted.page.request.view-access"
				description="Button label for requesting view access to a page"
				defaultMessage="Ask for access"
			/>
		);
	};

	const renderRequestAccessButton = () => (
		<FadeIn duration="large" key="primary-button-animation">
			{({ ...props }) => (
				<div {...props}>
					<Button
						onClick={handleRequestAccessClick}
						appearance="primary"
						isLoading={requestAccessButtonLoading}
					>
						{renderRequestAccessButtonMessage()}
					</Button>
				</div>
			)}
		</FadeIn>
	);

	const isLicensed = !!accessStatus && LICENSED_ACCESS_STATUSES.has(accessStatus);
	const renderGoHomeMessage = () => (
		<FormattedMessage
			id="no-permission.restricted.page.go-home"
			description="Secondary button text to navigate away from restricted content"
			defaultMessage="Go home"
		/>
	);
	const wrapWithHomePreloader = (button: React.ReactNode) => (
		<HoverPreloader
			name="restricted-page-home"
			preloadUrl={HOME.toUrl()}
			preloadOptions={PreloadingPresets.everything}
		>
			{button}
		</HoverPreloader>
	);
	const goHomeButtonProps = {
		href: HOME.toUrl(),
		children: renderGoHomeMessage(),
		onClick: handleGoHomeClick,
	};

	const canRenderPreloadedMohitiActions =
		isPendingRequestResolvedForSSR &&
		expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true);

	// Matches renderButtonOrExistingRequestText()
	const canRenderRequestAction =
		!areAccessRequestSettingsLoading &&
		(!isLoadingForFirstTime || canRenderPreloadedMohitiActions) &&
		!pendingAccessRequestExistsError &&
		(!renderServerPlaceholder || canRenderPreloadedMohitiActions) &&
		isLicensed;

	const shouldRenderWaitingForAccess =
		accessType !== 'edit' &&
		Boolean(contentId) &&
		canRenderRequestAction &&
		pendingRequestExists &&
		expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true);

	const hasCustomMessage = useMemo(() => hasCustomAccessMessage(messageContent), [messageContent]);
	// Gated on the Mohiti variant because control has no "Go home" button at all, so a control arm
	// would be a dead-end screen nobody designed.
	const showAccessRequestsDisabledScreen =
		isAccessRequestsDisabled &&
		expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true);
	const currentIllustration = (() => {
		if (showAccessRequestsDisabledScreen) {
			return restrictedContentMohitiIllustration;
		}

		if (accessType === 'edit') {
			return expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)
				? requestEditAccessMohitiIllustration
				: requestEditAccessIllustration;
		}

		if (expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
			return restrictedContentMohitiIllustration;
		}

		return restrictedContentIllustration;
	})();

	const renderButtonOrExistingRequestText = () => {
		if (
			areAccessRequestSettingsLoading ||
			isLoadingForFirstTime ||
			pendingAccessRequestExistsError ||
			renderServerPlaceholder ||
			!isLicensed
		) {
			return <Box xcss={styles.buttonPlaceholder} />;
		}

		if (pendingRequestExists || requestSent) {
			return renderRequestSentConfirmation();
		}

		return renderRequestAccessButton();
	};

	const renderManualRequestAccessMessage = () => (
		<FormattedMessage
			id="no-permission.restricted.page.manual.request.access.from.author"
			description="Instructions to manually request access to a restricted page from the page's author"
			defaultMessage="Only some people can view this. If you think you should have access, try asking the person who sent you here for help."
		/>
	);

	const renderViewAccessMessage = () => {
		if (expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
			return (
				<FormattedMessage
					id="no-permission.restricted.page.view.request.access.mohiti"
					description="Message displayed for restricted view content in the Mohiti restricted content experience"
					defaultMessage="You don’t have permission to view this content. If you want to view it, you can ask the owner."
				/>
			);
		}

		return (
			<FormattedMessage
				id="no-permission.restricted.page.view.request.access.from.author"
				description="Link to request viewing access to a restricted page"
				defaultMessage="Only some people can view this content. If you want access, you can ask the owner."
			/>
		);
	};

	const getButtonAlignment = (): 'default' | 'center' => {
		if (expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
			return 'center';
		}

		return 'default';
	};

	const renderInlineGoHomeButton = () => {
		if (!expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
			return null;
		}

		return wrapWithHomePreloader(
			<LinkButton href={HOME.toUrl()} onClick={handleGoHomeClick}>
				{renderGoHomeMessage()}
			</LinkButton>,
		);
	};

	const renderAccessRequestMessage = () => {
		if (accessType !== 'edit') {
			return renderViewAccessMessage();
		}

		if (expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)) {
			return (
				<FormattedMessage
					id="no-permission.restricted.page.edit.request.access.mohiti"
					description="Message displayed for restricted edit content in the Mohiti restricted content experience"
					defaultMessage="You don’t have permission to edit this content. If you want to edit it, you can ask the owner."
				/>
			);
		}

		return (
			<FormattedMessage
				id="no-permission.restricted.page.edit.request.access.from.author"
				description="Link to request editing access to a restricted page"
				defaultMessage="Only some people can edit this content. If you want to edit it, you can ask the owner."
			/>
		);
	};

	const renderAccessRequestButtons = () => {
		if (accessType === 'edit') {
			return (
				<ButtonContainer isMobile={shouldShowMobileWeb()} align={getButtonAlignment()}>
					{editDeniedButCanView && (
						<LinkButton href={viewLink!} onClick={makeLinkClickedHandler('main')}>
							<FormattedMessage
								id="no-permission.restricted.page.view-content"
								description="Link button label for viewing a page/blog/whiteboard/etc. the user can't edit"
								defaultMessage="View content"
							/>
						</LinkButton>
					)}
					{renderButtonOrExistingRequestText()}
					{!editDeniedButCanView ? renderInlineGoHomeButton() : null}
				</ButtonContainer>
			);
		}

		return (
			<ButtonContainer isMobile={shouldShowMobileWeb()} align={getButtonAlignment()}>
				{renderButtonOrExistingRequestText()}
				{renderInlineGoHomeButton()}
			</ButtonContainer>
		);
	};

	/** Renders the access request call to action and message */
	const renderAccessRequest = () => {
		if (!contentId) {
			return renderManualRequestAccessMessage();
		}

		return (
			<>
				{renderAccessRequestMessage()}
				{renderAccessRequestButtons()}
			</>
		);
	};

	const canRenderMohitiRequestActions =
		contentId &&
		accessType !== 'edit' &&
		canRenderRequestAction &&
		!pendingRequestExists &&
		!requestSent &&
		!editDeniedButCanView &&
		expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true);

	const shouldRenderMohitiRequestActions =
		canRenderMohitiRequestActions &&
		(accessType !== 'inherited' || expVal('confluence_access_request_updates', 'isEnabled', false));

	const isPendingRequestUnresolved =
		accessType !== 'edit' &&
		Boolean(contentId) &&
		(isLoadingForFirstTime || renderServerPlaceholder);

	// Whether to show the "waiting for access" title/message.
	// When the pending-request status is preloaded at SSR we render the resolved message in the server
	// HTML; when it isn't we render the default request-access message and swap post-hydration.
	const shouldRenderWaitingForAccessMessage =
		accessType !== 'edit' &&
		Boolean(contentId) &&
		isLicensed &&
		!pendingAccessRequestExistsError &&
		isPendingRequestResolvedForSSR &&
		pendingRequestExists &&
		expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true);

	// Default "ask the owner for access" message for the Mohiti view screen, shown whenever we're not
	// showing the "waiting for access" message — including while the lookup loads (SSR) so text never
	// blanks. requestSent is excluded so it falls through to the inline "Access requested" confirmation.
	const canRenderMohitiDefaultMessage =
		accessType !== 'edit' &&
		Boolean(contentId) &&
		!requestSent &&
		expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true);

	const shouldRenderMohitiDefaultMessage =
		canRenderMohitiDefaultMessage &&
		(accessType !== 'inherited' || expVal('confluence_access_request_updates', 'isEnabled', false));

	const hasPendingAccessRequest =
		accessType !== 'edit' && Boolean(contentId) && canRenderRequestAction && pendingRequestExists;

	const errorMessage = (() => {
		if (showAccessRequestsDisabledScreen) {
			return (
				<AccessDisabledMessage
					messageContent={messageContent}
					fallback={formatMessage(accessType === 'edit' ? i18n.editFallback : i18n.viewFallback)}
				/>
			);
		}

		if (shouldRenderWaitingForAccessMessage) {
			return renderPendingAccessMessage();
		}

		if (shouldRenderMohitiDefaultMessage) {
			return renderAccessRequestMessage();
		}

		return renderMessage();
	})();

	const renderErrorTitle = () => {
		switch (true) {
			case showAccessRequestsDisabledScreen:
				// One stored message serves both restriction types, so a custom message collapses view
				// and edit onto the same title — as the admin settings UI does by hiding its view/edit
				// dropdown. Only the edit fallback keeps its own wording.
				return formatMessage(
					accessType === 'edit' && !hasCustomMessage ? i18n.editingLockedTitle : i18n.lockedTitle,
				);
			case shouldRenderWaitingForAccessMessage:
				return (
					<FormattedMessage
						id="no-permission.restricted.page.pending-access.title"
						description="Title displayed after the user has requested access to restricted content"
						defaultMessage="Your request is in the works"
					/>
				);
			default:
				return renderTitle();
		}
	};

	const primaryButtonProps = (() => {
		if (showAccessRequestsDisabledScreen) {
			return goHomeButtonProps;
		}

		if (shouldRenderWaitingForAccess) {
			return {
				children: (
					<FormattedMessage
						id="no-permission.restricted.page.pending-access.send-reminder"
						description="Primary button label for resending an access request reminder"
						defaultMessage="Send a reminder"
					/>
				),
				onClick: handleSendReminderClick,
				isDisabled: reminderSent,
				isLoading: requestAccessButtonLoading,
			};
		}

		if (shouldRenderMohitiRequestActions) {
			return {
				children: renderRequestAccessButtonMessage(),
				onClick: handleRequestAccessClick,
				isLoading: requestAccessButtonLoading,
			};
		}

		return undefined;
	})();

	const secondaryButtonProps =
		!showAccessRequestsDisabledScreen &&
		(shouldRenderWaitingForAccess || shouldRenderMohitiRequestActions)
			? goHomeButtonProps
			: undefined;

	useEffect(() => {
		// Defer the screen event until the pending-access-request lookup has settled, so
		// `hasPendingAccessRequest` reflects the user's actual request state rather than
		// the transient loading state.
		if (isPendingRequestUnresolved || areAccessRequestSettingsLoading) {
			return;
		}

		// Yes, it's always a 'page' viewed, even if contentType is something
		// else. It's just how this screen is registered.
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'restrictedPageViewed',
				action: 'viewed',
				actionSubject: 'page',
				actionSubjectId: 'restrictedPage',
				attributes: {
					accessType: accessType || 'unspecified',
					contentId,
					contentType,
					editDeniedButCanView,
					from,
					hasPendingAccessRequest,
				},
			},
		}).fire();
	}, [
		accessType,
		contentId,
		contentType,
		editDeniedButCanView,
		viewLink,
		createAnalyticsEvent,
		from,
		isPendingRequestUnresolved,
		hasPendingAccessRequest,
		areAccessRequestSettingsLoading,
	]);

	return (
		<>
			<ErrorPageComponent
				errorImage={currentIllustration.light}
				errorImageDark={currentIllustration.dark}
				layout={
					expValEquals('cc_mohiti_restricted_content_screen_ab_exp', 'isEnabled', true)
						? 'centered'
						: 'default'
				}
				errorTitle={renderErrorTitle()}
				errorMessage={errorMessage}
				primaryButtonProps={primaryButtonProps}
				secondaryButtonProps={secondaryButtonProps}
				// Only the locked screen's primary button is "Go home"; the request screens' primary is
				// the request action, which must not preload Home on hover.
				primaryButtonWrapper={showAccessRequestsDisabledScreen ? wrapWithHomePreloader : undefined}
				secondaryButtonWrapper={wrapWithHomePreloader}
			>
				{showSuperAdminGateway && <SuperAdminGateway type="page" contentId={contentId} />}
			</ErrorPageComponent>
			<ExperienceSuccess name={RESTRICTED_PAGE_EXPERIENCE} />
			<PageLoadEnd
				metric={INACCESSIBLE_PAGE_LOAD}
				customData={{ ...CUSTOM_ATTRIBUTES, contentId }}
			/>
		</>
	);
};

const RestrictedPageWithCanViewQuery = (props: RestrictedPageProps & WithFlagsProps) => {
	const { accessType, contentId } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const skipViewQuery = accessType !== 'edit';
	const spaceKey = useSpaceKey();
	const { replace } = useRouteActions();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isLoggedIn } = useSessionData();

	// No `contentId` means one of the ~30 admin/settings pages that render `<RestrictedPage />` with
	// no props; those are "you are not an admin" screens, where the content message would be wrong.
	// The anonymous skip must stay in step with `preloadAccessRequestSettings`.
	const {
		contentEnabled,
		contentMessageContent,
		loading: isAccessRequestSettingsLoading,
	} = useAccessRequestSettings({
		skip:
			!contentId ||
			!isLoggedIn ||
			!UNSAFE_expValEqualsNoExposure('cc_perms_ga_access_requests', 'isEnabled', true) ||
			!UNSAFE_expValEqualsNoExposure(
				'cc_mohiti_restricted_content_screen_ab_exp',
				'isEnabled',
				true,
			),
	});

	// While a view-denied requestor waits for their access request, poll for the
	// content's actual view access so we can refresh once it is (effectively)
	// granted, rather than relying on the access-decision signal alone.
	const pollForViewAccess =
		accessType === 'view' &&
		Boolean(contentId) &&
		expVal('confluence_access_request_updates', 'isEnabled', false);

	const { data, error, loading, stopPolling } = useQueryInterop<
		RestrictedPageWithCanViewQuery$data,
		RestrictedPageWithCanViewQuery$variables
	>(RestrictedPageWithCanViewQueryGraphQL, {
		variables: { id: contentId ?? '' },
		skip: skipViewQuery && !pollForViewAccess,
		fetchPolicy: 'network-only',
		pollInterval: pollForViewAccess ? 5000 : undefined,
		notifyOnNetworkStatusChange: pollForViewAccess,
	});

	// The content resolving to a non-draft means the current user can access it.
	// Drafts are excluded to match the existing viewability semantics.
	const contentResolvedAndViewable =
		Boolean(data?.singleContent) && data?.singleContent?.status !== 'draft';

	const editDeniedButCanView = !skipViewQuery && contentResolvedAndViewable;

	// In the view-poll case, the content becoming viewable means the requestor can
	// now access it; stop polling and refresh so the real content renders.
	const contentNowViewable = pollForViewAccess && contentResolvedAndViewable;
	useEffect(() => {
		if (contentNowViewable) {
			stopPolling();
			window.location.reload();
		}
	}, [contentNowViewable, stopPolling]);
	const viewLink = editDeniedButCanView
		? `${CONTEXT_PATH}${data?.singleContent?.links?.webui}`
		: null;
	const canBeSuperAdmin = Boolean(data?.user?.confluence?.roles?.canBeSuperAdmin);
	// Only redirect user if:
	// 1. They have a viewLink available.
	// 2. They can't be super admin.
	const shouldRedirectToView = spaceKey && viewLink && !canBeSuperAdmin;

	useEffect(() => {
		if (shouldRedirectToView) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'redirected',
					actionSubject: 'redirectToViewMode',
					source: 'restrictedPageScreen',
					attributes: {
						accessType,
						contentId,
					},
				},
			}).fire();

			experienceTracker.succeed({
				name: RESTRICTED_PAGE_EXPERIENCE,
			});

			if (expValEquals('confluence_edit_page_restricted_redirect_ttvc_fix', 'isEnabled', true)) {
				// Abort the in-flight edit-page UFO interaction so this redirect isn't recorded as a
				// successful edit-page load.
				abortAll('transition');
			}

			replace(
				VIEW_PAGE.toUrl(
					{
						spaceKey,
						contentId,
					},
					{
						query: {
							redirectedFromRestrict: 'true',
						},
					},
				),
			);
		}
	}, [
		createAnalyticsEvent,
		contentId,
		shouldRedirectToView,
		accessType,
		spaceKey,
		replace,
		experienceTracker,
	]);

	// In the view-poll case the restricted screen must render immediately while the
	// access check runs in the background, so loading is only blocking for the
	// edit case's one-shot redirect check.
	if (loading && !pollForViewAccess) {
		return <ShowSpinnerAfterDelay />;
	} else {
		/**
		 * Usually, if the user doesn't have permission to view the page, the
		 * query succeeds with `singleContent: null`. But occasionally we get a
		 * 401 or 403, and we'd consider those to be expected errors.
		 */
		let hasUnhandledError = false;
		if (error) {
			if (is401(error)) markErrorAsHandled(error);
			else if (is403(error)) markErrorAsHandled(error);
			else hasUnhandledError = true;
		}

		return shouldRedirectToView ? (
			<ShowSpinnerAfterDelay />
		) : (
			<>
				{hasUnhandledError && (
					<>
						<ExperienceFailure name={RESTRICTED_PAGE_EXPERIENCE} error={error!} />
						<ErrorDisplay error={error!} />
					</>
				)}
				<RestrictedPageComponent
					{...props}
					editDeniedButCanView={editDeniedButCanView}
					viewLink={viewLink}
					isAccessRequestsDisabled={contentEnabled === false}
					messageContent={contentMessageContent}
					areAccessRequestSettingsLoading={isAccessRequestSettingsLoading}
				/>
			</>
		);
	}
};

export const RestrictedPage = (props: RestrictedPageProps): React.JSX.Element => {
	if (
		props.contentId &&
		window['__SPACE_RESTRICTED_ERROR__'] === props.contentId &&
		expVal('confluence_frontend_ssr_space_not_found', 'isEnabled', false)
	) {
		return (
			<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
				<RestrictedSpace contentId={props.contentId} />
			</ErrorBoundary>
		);
	}

	return (
		<ErrorBoundary attribution={Attribution.ADMIN_EXPERIENCE}>
			<ExperienceStart name={RESTRICTED_PAGE_EXPERIENCE} />
			<Subscribe to={[FlagsStateContainer]}>
				{(flags: FlagsStateContainer) => (
					<RestrictedPageWithCanViewQuery {...props} flags={flags} />
				)}
			</Subscribe>
		</ErrorBoundary>
	);
};
